import React, { createContext, ReactNode, useCallback, useContext, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Typography,
    useTheme,
    Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SyncIcon from "@mui/icons-material/Sync";
import EditIcon from "@mui/icons-material/Edit";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export interface ErrorActionConfig {
    label: string;
    tip?: string;
    icon?: ReactNode;
    onClick: (err: ErrorItem) => void;
}

export interface ErrorItem {
    id?: string | number;
    title?: string;
    message: string;
    color?: string;
    type?: "error" | "warning" | "info" | "none";
    actions?: ErrorActionConfig[];
}

export interface ErrorListDialogProps {
    open: boolean;
    onClose: () => void;
    title?: string|JSX.Element;
    description?: string|JSX.Element;
    errors: ErrorItem[];
    globalActions?: ErrorActionConfig[];
}

export const ErrorListDialog: React.FC<ErrorListDialogProps> = ({
    open,
    onClose,
    title = "Errors",
    description,
    errors,
    globalActions = [],
}) => {
    const theme = useTheme();

    const onHandleClose = (event:object, reason: "backdropClick" | "escapeKeyDown") => {
        if(reason == 'backdropClick' || reason == 'escapeKeyDown') {
            return;
        }
        onClose && onClose();
    }

    return (
        <Dialog open={open} onClose={onHandleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ textAlign: "center" }}>{title}</DialogTitle>
            <DialogContent dividers sx={{ p: 0, minHeight:240, maxHeight: 400 }}>
                {description && (
                    <Typography variant="body2" sx={{ p: 2 }}>
                        {description}
                    </Typography>
                )}

                <List dense sx={{ overflowY: "auto", border: "1px solid", borderColor: theme.palette.divider, ml:'8px', mr:'8px' }}>
                    {errors.map((err, idx) => {
                        const key = err.id ?? idx;
                        const actions = [...(err.actions ?? []), ...globalActions];

                        return (
                            <ListItem
                                key={key}
                                secondaryAction={
                                    actions.map((act, i) => (
                                        <Tooltip key={i} title={act.tip||act.label}>
                                        <span>
                                            <IconButton
                                                key={i}
                                                edge="end"
                                                aria-label={act.label}
                                                
                                                onClick={() => act.onClick(err)}
                                                sx={{ ml: theme.spacing(1) }}
                                            >
                                                {act.icon}
                                            </IconButton>
                                        </span>
                                        </Tooltip>
                                    ))
                                }
                                sx={{ backgroundColor: err.color, borderBottom: `1px solid ${theme.palette.divider}` }}
                            >
                                {err.type === "error" && <ErrorOutlineIcon color="error" sx={{ mr: 1 }} />}
                                {err.type === "warning" && <WarningAmberOutlinedIcon color="warning" sx={{ mr: 1 }} />}
                                {err.type === "info" && <InfoOutlinedIcon color="info" sx={{ mr: 1 }} />}
                                <ListItemText
                                    primary={<b>{err.title}</b>}
                                    secondary={
                                        err.message.split('\n').map((line, i) => (
                                            <React.Fragment key={i}>
                                                {line}
                                                {i < err.message.split('\n').length - 1 && <br />}
                                            </React.Fragment>
                                        ))
                                    }
                                />
                            </ListItem>
                        );
                    })}
                </List>
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: "center" }}>
                <Button onClick={onClose} variant="contained" fullWidth>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const copyAction: ErrorActionConfig = {
    label: "Copy",
    tip:'Copy the error text to clipboard',
    icon: <ContentCopyIcon fontSize="inherit" />,
    onClick: (err) => {
        navigator.clipboard.writeText(`${err.title ? err.title + '\n' : err.title} ${err.message}`);
    },
};

export const syncAction: ErrorActionConfig = {
    label: "Sync",
    icon: <SyncIcon fontSize="inherit" />,
    onClick: (err) => {
        console.info("Sync requested for", err);
    },
};

export const editAction: ErrorActionConfig = {
    label: "Edit",
    icon: <EditIcon fontSize="inherit" />,
    onClick: (err) => {
        console.info("Edit requested for", err);
    },
};

interface ErrorDialogContextValue {
    show: (options: Omit<ErrorListDialogProps, "open" | "onClose">) => void;
    hide: () => void;
}

const ErrorListDialogContext = createContext<ErrorDialogContextValue | null>(null);

export const useErrorListDialog = (): ErrorDialogContextValue => {
    const ctx = useContext(ErrorListDialogContext);
    if (!ctx) {
        throw new Error("useErrorDialog must be used within an <ErrorDialogProvider>");
    }
    return ctx;
};

export const ErrorDialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [dialogOptions, setDialogOptions] = useState<
        Omit<ErrorListDialogProps, "open" | "onClose"> | null
    >(null);

    const show: ErrorDialogContextValue["show"] = (options) => setDialogOptions(options);
    const hide = () => setDialogOptions(null);

    return (
        <ErrorListDialogContext.Provider value={{ show, hide }}>
            {children}
            {dialogOptions && (
                <ErrorListDialog open onClose={hide} {...dialogOptions} />
            )}
        </ErrorListDialogContext.Provider>
    );
};

export interface UseErrorQueueResult {
    title: string|JSX.Element|undefined;
    setTitle:(v:string|JSX.Element|undefined)=>void;
    body: string|JSX.Element|undefined;
    setBody:(v:string|JSX.Element|undefined)=>void;
    errors: ErrorItem[];
    enqueue: (errs: ErrorItem | ErrorItem[]) => void;
    clear: () => void;
    dialogOpen: boolean;
    setDialogOpen: (v: boolean) => void;
}

export function useErrorQueue(): UseErrorQueueResult {
    const [title, setTitle] = useState<string|JSX.Element|undefined>();
    const [body, setBody] = useState<string|JSX.Element|undefined>();
    const [queue, setQueue] = useState<ErrorItem[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);

    const enqueue = useCallback((errs: ErrorItem | ErrorItem[]) => {
        const normalized = Array.isArray(errs) ? errs : [errs];
        setQueue((prev) => [...prev, ...normalized]);
        setDialogOpen(true);
    }, []);

    const clear = useCallback(() => {
        setTitle(undefined);
        setBody(undefined);
        setQueue([]);
        setDialogOpen(false);
    }, []);

    return { title, setTitle, body, setBody, errors: queue, enqueue, clear, dialogOpen, setDialogOpen };
}